import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { Flex, Input } from 'antd';
import { useMemo, useState } from 'react';

import { $Color, Color } from '@suntex.core/color';
import { Entities_Modules_Module } from '@suntex.fim/api-client';
import {
  $GradeLevel,
  $LearningStage,
  FavoriteGamesAction,
  GradeLevel,
  LearningStage,
} from '@suntex.fim/enums';
import {
  ModuleTagging,
  buildUnderConstructionSet,
} from '@suntex.fim/framework';
import { StrictNodeModel, rootRadialData } from '@suntex.fim/hub';
import { Button, Card, Toolbar } from '@suntex.fim/ui';
import { SearchIcon } from '@suntex.fim/ui/icons';

import { useModuleSearch, useModuleStarValue } from '../../hooks';
import '../../style/ContentLibrary.scss';
import { buildLearningStageModuleMatcher } from '../../utils/learningStageModules';
import {
  ModuleTreeEntry,
  collectModuleEntries,
  getLabelText,
} from '../../utils/moduleTree';
import { FavoriteButton } from '../FavoriteButton';
import { ModuleIcon } from '../ModuleIcon';

import { ContentModuleCard } from './ContentModuleCard';

export interface ContentLibraryProps {
  modules: Entities_Modules_Module[];
  moduleTagging: readonly ModuleTagging[];
  /** The child's grade level; enables the Stages of Learning filter. */
  gradeLevel?: GradeLevel;
}

const GRADE_LEVEL_STAGE = LearningStage.GotIt;

interface ContentCategory {
  key: string;
  label: string;
  color: Color;
  hexColor: string;
  node: StrictNodeModel;
  entries: ModuleTreeEntry[];
}

export function ContentLibrary({
  modules,
  moduleTagging,
  gradeLevel,
}: ContentLibraryProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showGradeStageOnly, setShowGradeStageOnly] = useState(false);

  const getModuleStarValue = useModuleStarValue();
  const {
    searchTerm,
    setSearchTerm,
    showFavoritesOnly,
    setShowFavoritesOnly,
    isFavorite,
    toggleFavorite,
    matchesSearch,
  } = useModuleSearch(modules, moduleTagging);

  const categories = useMemo(() => {
    return buildCategories(modules, moduleTagging);
  }, [modules, moduleTagging]);

  const underConstructionSet = useMemo(
    () => buildUnderConstructionSet(moduleTagging),
    [moduleTagging],
  );

  const gradeStageFilter = useMemo(
    () =>
      gradeLevel === undefined
        ? undefined
        : {
            label: `SoL ${$GradeLevel.describe(gradeLevel).label} "${$LearningStage.describe(GRADE_LEVEL_STAGE).label}"`,
            matches: buildLearningStageModuleMatcher(
              moduleTagging,
              gradeLevel,
              GRADE_LEVEL_STAGE,
            ),
          },
    [gradeLevel, moduleTagging],
  );
  const isGradeStageFilterActive =
    showGradeStageOnly && gradeStageFilter !== undefined;

  const visibleCategories = useMemo(() => {
    return categories
      .map((cat) => ({
        ...cat,
        entries:
          selectedCategory !== null && cat.key !== selectedCategory
            ? []
            : cat.entries.filter(
                ({ module, node }) =>
                  matchesSearch(module.slug, node.moduleVariant) &&
                  (!isGradeStageFilterActive ||
                    gradeStageFilter.matches(module.slug, node.moduleVariant)),
              ),
      }))
      .filter((c) => c.entries.length > 0);
  }, [
    categories,
    selectedCategory,
    matchesSearch,
    isGradeStageFilterActive,
    gradeStageFilter,
  ]);

  const toggleFavoritesOnly = () => {
    setShowFavoritesOnly((v) => !v);
    setShowGradeStageOnly(false);
  };

  const toggleGradeStageOnly = () => {
    setShowGradeStageOnly((v) => !v);
    setShowFavoritesOnly(false);
  };

  const emptyMessage = isGradeStageFilterActive
    ? `No ${gradeStageFilter.label} modules found.`
    : showFavoritesOnly
      ? 'No favorite modules found. Heart a game to add it here.'
      : 'No modules match your search.';

  const handleToggleFavorite = (
    slug: string,
    variant: number | undefined,
    action: FavoriteGamesAction.Add | FavoriteGamesAction.Remove,
  ) => {
    void toggleFavorite(slug, variant, action);
  };

  return (
    <div className="content-library">
      <Toolbar
        left={<h4>Content Library</h4>}
        right={
          <Flex gap={8} align="center">
            {gradeStageFilter && (
              <Button
                intent="icon"
                icon={
                  isGradeStageFilterActive ? (
                    <FilterFilled />
                  ) : (
                    <FilterOutlined />
                  )
                }
                onClick={toggleGradeStageOnly}
                aria-label={
                  isGradeStageFilterActive
                    ? 'Show all modules'
                    : `Show ${gradeStageFilter.label} modules only`
                }
                title={`${gradeStageFilter.label} modules`}
              />
            )}
            <FavoriteButton
              active={showFavoritesOnly}
              onClick={toggleFavoritesOnly}
              aria-label={
                showFavoritesOnly ? 'Show all modules' : 'Show favorites only'
              }
            />
            <Input
              placeholder="Search"
              prefix={<SearchIcon />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Flex>
        }
      />

      <Flex gap={8} wrap className="content-library__category-filters">
        <Button
          size="small"
          intent={selectedCategory === null ? 'primary' : 'secondary'}
          onClick={() => setSelectedCategory(null)}
        >
          All
        </Button>
        {categories.map((cat) => (
          <Button
            key={cat.key}
            size="small"
            intent={selectedCategory === cat.key ? 'primary' : 'secondary'}
            style={
              selectedCategory === cat.key
                ? {
                    backgroundColor: cat.hexColor,
                    borderColor: 'transparent',
                  }
                : undefined
            }
            onClick={() =>
              setSelectedCategory((prev) => (prev === cat.key ? null : cat.key))
            }
          >
            {cat.label}
          </Button>
        ))}
      </Flex>

      {visibleCategories.length === 0 ? (
        <div className="content-library__empty">{emptyMessage}</div>
      ) : (
        visibleCategories.map((cat) => (
          <Card
            key={cat.key}
            title={
              <Flex vertical={false} justify="start" align="center">
                <ModuleIcon node={cat.node} size="lg" />
                <h5 style={{ fontWeight: 'inherit' }}>{cat.label}</h5>
              </Flex>
            }
            color={cat.color}
            className="content-library__category"
          >
            <div className="content-library__cards">
              {cat.entries.map(({ module, node }) => (
                <ContentModuleCard
                  key={node.key}
                  module={module}
                  node={node}
                  categoryColor={cat.color}
                  isFavorite={isFavorite(module.slug, node.moduleVariant)}
                  starValue={getModuleStarValue(node)}
                  underConstruction={underConstructionSet.has(module.slug)}
                  onToggleFavorite={handleToggleFavorite}
                />
              ))}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildCategories(
  modules: Entities_Modules_Module[],
  moduleTagging: readonly ModuleTagging[],
): ContentCategory[] {
  const moduleMap = new Map(modules.map((m) => [m.slug, m]));
  const categoriesRoot = rootRadialData.children.find(
    (c) => c.tag === 'modules',
  );
  if (!categoriesRoot) return [];
  return categoriesRoot.children
    .filter((c) => !c.hidden)
    .map((categoryNode) => {
      const color = $Color.from(categoryNode.color);

      return {
        key: categoryNode.tag ?? categoryNode.key,
        label:
          getLabelText(categoryNode) ?? categoryNode.tag ?? categoryNode.key,
        color,
        hexColor: $Color.toHexString(color),
        node: categoryNode,
        entries: collectModuleEntries(categoryNode, moduleMap, moduleTagging),
      };
    })
    .filter((c) => c.entries.length > 0);
}
